/**
 * playerBinding — wire a mounted DotPlayer to the analytics video session and
 * emit REAL quality-of-experience events from native HTMLMediaElement signals.
 *
 * DotPlayer renders a video.js player, so the same-origin `<video>` element
 * lives in the DOM under the mount target (e.g. `#pdp-player video`, id
 * `content-video_html5_api`). We resolve it defensively — first any handle the
 * player object exposes, then the mount container, then any `<video>` — because
 * the exact object shape varies across DotPlayer builds. From that element we
 * get truthful QoE without depending on player internals:
 *   - startup   : bind → first `playing`             → video_startup (startup_ms)
 *   - rebuffer  : `waiting` → next `playing`          → video_rebuffer (rebuffer_seconds)
 *   - seek      : `seeking`/`seeked` (+ direction)    → video_seek
 *   - quality   : `resize`/`loadedmetadata` res change→ video_quality_change (resolution)
 *   - error     : `error` (+ MediaError code)         → video_error
 * plus engagement (start/progress/complete/heartbeat/pause) via VideoSession in
 * `realQoE` mode so its heuristics don't double-count what we measure for real.
 *
 * QoE-over-time is carried by those discrete events; the heartbeat stays a lean
 * liveness ping. The per-playback QoE counters (rebuffer/seek/error/bitrate)
 * accumulate here and roll into the single `video_complete` summary via a
 * `summary()` provider handed to VideoSession. We also flush that summary on
 * `pagehide` so a tab closed mid-playback still yields one completion row.
 *
 * Fully defensive: any failure is swallowed so analytics can never break
 * playback. Returns a cleanup function; call it on unmount.
 *
 * @param {Object} player - the video.js player handed over by BkfcPlayer's onReady
 * @param {Object} meta - content context (content_id/title/type/series_id/…, duration)
 * @param {(name: string, params: Object) => void} emit - analytics sink
 * @param {Object} [opts]
 * @param {string} [opts.targetSelector] - the player mount container (e.g. '#pdp-player')
 * @returns {() => void} cleanup
 */

import VideoSession from './videoSession';
import { EVENTS, contentContext } from './events';

const round = n => Math.round(n * 10) / 10;
const noop = () => {};

// MediaError.code → readable label (spec-defined codes 1–4).
const MEDIA_ERROR = {
  1: 'aborted',
  2: 'network',
  3: 'decode',
  4: 'src_not_supported',
};

/** Attach engagement + QoE listeners to a resolved <video> element. */
function attach(video, meta, emit) {
  const ctx = contentContext(meta);

  // Reach the video.js player instance (DotPlayer renders one) for real
  // streaming telemetry the raw <video> element can't give us: rendition
  // bitrate, dropped frames and bandwidth. All optional and guarded.
  const vjs =
    (video.closest && video.closest('.video-js') && video.closest('.video-js').player) || null;
  let ql = null;
  try {
    ql = vjs && typeof vjs.qualityLevels === 'function' ? vjs.qualityLevels() : null;
  } catch (e) {
    ql = null;
  }
  let vhs = null;
  try {
    const tech = vjs && typeof vjs.tech === 'function' ? vjs.tech(true) : null;
    vhs = tech && (tech.vhs || tech.hls);
  } catch (e) {
    vhs = null;
  }

  const bitrateKbps = () => {
    try {
      const l = ql && ql.selectedIndex >= 0 ? ql[ql.selectedIndex] : null;
      return l && l.bitrate ? Math.round(l.bitrate / 1000) : undefined;
    } catch (e) {
      return undefined;
    }
  };
  const bandwidthKbps = () => (vhs && vhs.bandwidth ? Math.round(vhs.bandwidth / 1000) : undefined);

  // --- Per-playback QoE counters -------------------------------------------
  // Accumulated as the native events fire and rolled into the single
  // video_complete summary — NOT onto every heartbeat (which stays lean).
  let startupMs; // mount → first frame
  let rebufferCount = 0;
  let rebufferMsTotal = 0;
  let seekCount = 0;
  let errorCount = 0;
  let qualitySwitches = 0;
  let minBitrate = 0; // lowest rendition kbps actually played
  // Time-weighted average bitrate: Σ(bitrate × dwell) / Σ(dwell).
  let curBitrate = 0;
  let bitrateSince = 0;
  let brWeightSum = 0;
  let brWeightTime = 0;

  const accrueBitrate = now => {
    if (curBitrate > 0 && bitrateSince > 0) {
      const dwell = now - bitrateSince;
      brWeightSum += curBitrate * dwell;
      brWeightTime += dwell;
    }
    bitrateSince = now;
  };
  const setBitrate = (now, kbps) => {
    accrueBitrate(now);
    if (kbps > 0) {
      curBitrate = kbps;
      if (minBitrate === 0 || kbps < minBitrate) {
        minBitrate = kbps;
      }
    }
  };

  const droppedRatio = () => {
    try {
      const q = video.getVideoPlaybackQuality && video.getVideoPlaybackQuality();
      if (q && q.totalVideoFrames) {
        return round((q.droppedVideoFrames / q.totalVideoFrames) * 100);
      }
    } catch (e) {
      // ignore
    }
    return undefined;
  };

  // Rolled-up QoE for the single per-playback video_complete summary.
  const summary = () => {
    accrueBitrate(Date.now());
    const avg = brWeightTime > 0 ? Math.round(brWeightSum / brWeightTime) : curBitrate || undefined;
    const dr = droppedRatio();
    const out = {
      rebuffer_count: rebufferCount,
      rebuffer_seconds_total: round(rebufferMsTotal / 1000),
      seek_count: seekCount,
      error_count: errorCount,
      quality_switches: qualitySwitches,
    };
    if (startupMs !== undefined) out.startup_ms = startupMs;
    if (minBitrate > 0) out.min_bitrate_kbps = minBitrate;
    if (avg !== undefined) out.avg_bitrate_kbps = avg;
    if (dr !== undefined) out.dropped_ratio = dr;
    return out;
  };

  const session = new VideoSession(meta, { emit, realQoE: true, summary });
  const emitQoE = (name, params) => {
    try {
      emit(name, { ...ctx, ...params });
    } catch (e) {
      // never break playback
    }
  };

  const t0 = Date.now();
  // If we attach to an already-playing video (autoplay may have started before
  // we bound), we can't measure a true startup — don't emit a bogus one.
  let startupSent = !video.paused && (video.currentTime || 0) > 0.5;
  let rebufferStart = 0;
  let lastTime = video.currentTime || 0;
  let seekFrom = 0;
  let lastResolution = video.videoWidth ? `${video.videoWidth}x${video.videoHeight}` : '';

  const safe =
    fn =>
    (...args) => {
      try {
        fn(...args);
      } catch (e) {
        // swallow — analytics must never throw into the player
      }
    };

  const onTimeUpdate = safe(() => {
    lastTime = video.currentTime || 0;
    session.onTimeUpdate(lastTime, video.duration);
  });
  const onPause = safe(() => session.onPause(video.currentTime || 0));
  const onEnded = safe(() => session.onEnded(video.currentTime || 0));

  const onPlaying = safe(() => {
    const now = Date.now();
    if (!startupSent) {
      startupSent = true;
      startupMs = Math.round(now - t0);
      const b = bitrateKbps();
      const bw = bandwidthKbps();
      setBitrate(now, b || 0);
      emitQoE(EVENTS.VIDEO_STARTUP, {
        startup_ms: startupMs,
        ...(b !== undefined ? { bitrate_kbps: b } : {}),
        ...(bw !== undefined ? { bandwidth_kbps: bw } : {}),
      });
    }
    if (rebufferStart) {
      rebufferCount += 1;
      rebufferMsTotal += now - rebufferStart;
      emitQoE(EVENTS.VIDEO_REBUFFER, {
        rebuffer_seconds: round((now - rebufferStart) / 1000),
        position: round(video.currentTime || 0),
      });
      rebufferStart = 0;
    }
  });
  // Only count stalls after playback has started (ignore initial buffering,
  // already captured as startup).
  const onWaiting = safe(() => {
    if (startupSent && !rebufferStart) {
      rebufferStart = Date.now();
    }
  });

  const onSeeking = safe(() => {
    seekFrom = lastTime;
  });
  const onSeeked = safe(() => {
    const to = video.currentTime || 0;
    if (Math.abs(to - seekFrom) < 0.5) {
      return; // no-op seek (e.g. resume nudge)
    }
    seekCount += 1;
    emitQoE(EVENTS.VIDEO_SEEK, {
      seek_direction: to < seekFrom ? 'rewind' : 'forward',
      seek_from: round(seekFrom),
      seek_to: round(to),
      seek_seconds: round(Math.abs(to - seekFrom)),
    });
  });

  const onResize = safe(() => {
    if (!video.videoWidth || !video.videoHeight) {
      return;
    }
    const resolution = `${video.videoWidth}x${video.videoHeight}`;
    if (resolution === lastResolution) {
      return;
    }
    const previous = lastResolution;
    lastResolution = resolution;
    // The first resolution is baseline, not a "change". When qualityLevels is
    // available we emit the richer bitrate-based change instead (see onQuality).
    if (previous && !ql) {
      emitQoE(EVENTS.VIDEO_QUALITY_CHANGE, { resolution, from_resolution: previous });
    }
  });

  // Real rendition switch from video.js qualityLevels — carries actual bitrate.
  let lastBitrate = 0;
  const onQuality = safe(() => {
    if (!ql || ql.selectedIndex < 0) {
      return;
    }
    const l = ql[ql.selectedIndex];
    if (!l) {
      return;
    }
    const bitrate_kbps = l.bitrate ? Math.round(l.bitrate / 1000) : undefined;
    if (bitrate_kbps === lastBitrate) {
      return; // dedupe repeat 'change' fires for the same rendition
    }
    const from_bitrate_kbps = lastBitrate || undefined;
    const isBaseline = !lastBitrate; // first rendition is the startup pick, not a switch
    lastBitrate = bitrate_kbps || 0;
    setBitrate(Date.now(), bitrate_kbps || 0);
    if (isBaseline) {
      return; // seed only — the initial bitrate already rides on video_startup
    }
    qualitySwitches += 1;
    const bw = bandwidthKbps();
    emitQoE(EVENTS.VIDEO_QUALITY_CHANGE, {
      bitrate_kbps,
      resolution: l.width && l.height ? `${l.width}x${l.height}` : undefined,
      from_bitrate_kbps,
      ...(bw !== undefined ? { bandwidth_kbps: bw } : {}),
    });
  });
  if (ql && typeof ql.on === 'function') {
    ql.on('change', onQuality);
  }

  const onError = safe(() => {
    const err = video.error;
    errorCount += 1;
    emitQoE(EVENTS.VIDEO_ERROR, {
      error_code: err && err.code,
      message: (err && (err.message || MEDIA_ERROR[err.code])) || 'unknown',
    });
  });

  const listeners = [
    ['timeupdate', onTimeUpdate],
    ['pause', onPause],
    ['ended', onEnded],
    ['playing', onPlaying],
    ['waiting', onWaiting],
    ['seeking', onSeeking],
    ['seeked', onSeeked],
    ['resize', onResize],
    ['loadedmetadata', onResize],
    ['error', onError],
  ];
  listeners.forEach(([name, fn]) => video.addEventListener(name, fn));

  // Flush the per-playback summary when the page is torn down (navigation, tab/
  // window close, bfcache). `pagehide` is the reliable teardown signal and —
  // unlike `visibilitychange` — does NOT fire on an ordinary tab switch, so we
  // never fabricate a "complete" for a viewer who tabs away mid-fight and comes
  // back (desktop playback continues while hidden). onEnded is idempotent
  // (completeFired) and no-ops before playback starts, so a later natural end
  // can't double-count. Best-effort delivery given the GET-image beacon; a hard
  // kill with no pagehide is covered by the 60s heartbeat watch-time floor.
  const onExit = safe(() => session.onEnded(video.currentTime || 0));
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onExit);
  }

  return () => {
    try {
      listeners.forEach(([name, fn]) => video.removeEventListener(name, fn));
      if (ql && typeof ql.off === 'function') {
        ql.off('change', onQuality);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', onExit);
      }
    } catch (e) {
      // ignore
    }
  };
}

export default function bindPlayer(player, meta = {}, emit = noop, opts = {}) {
  let detach = noop;
  let cancelled = false;
  let tries = 0;
  let timer = null;

  const resolveVideo = () => {
    // 1) A handle the player object exposes directly (varies by build).
    const direct = player && player.player && player.player.current && player.player.current.player;
    if (direct && typeof direct.addEventListener === 'function') {
      return direct;
    }
    if (typeof document === 'undefined') {
      return null;
    }
    // 2) The <video> inside the mount container (video.js DOM).
    if (opts.targetSelector) {
      const el = document.querySelector(`${opts.targetSelector} video`);
      if (el) return el;
    }
    // 3) Last resort: any <video> on the page.
    return document.querySelector('video');
  };

  const tryAttach = () => {
    if (cancelled) {
      return;
    }
    let video = null;
    try {
      video = resolveVideo();
    } catch (e) {
      video = null;
    }
    if (video && typeof video.addEventListener === 'function') {
      try {
        detach = attach(video, meta, emit);
      } catch (e) {
        detach = noop;
      }
      return;
    }
    // The tech <video> can attach a beat after the player reports "loaded".
    if (tries++ < 12) {
      timer = setTimeout(tryAttach, 400);
    }
  };

  try {
    tryAttach();
  } catch (e) {
    // never break playback
  }

  return () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
    }
    try {
      detach();
    } catch (e) {
      // ignore
    }
  };
}
